// src/features/agent-builder/components/UploadedFileListItem.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, AlertTriangle, X, RotateCcw } from 'lucide-react';
import { Badge, IconButton, Tooltip, Button } from '@dopomogai/ui';
import type { UploadableFile } from '@dopomogai/types';
import clsx from 'clsx';

interface UploadedFileListItemProps {
  fileData: UploadableFile;
  onRemove: (fileId: string) => void;
  onRetry?: (fileId: string) => void;
}

export const UploadedFileListItem: React.FC<UploadedFileListItemProps> = ({
  fileData,
  onRemove,
  onRetry,
}) => {
  // [KEY FIX] Tell useTranslation to use the 'agentBuilder' namespace.
  const { t } = useTranslation('agentBuilder');

  const getStatusBadgeColor = (): 'green' | 'blue' | 'yellow' | 'red' | 'gray' => {
    switch (fileData.status) {
      case 'ready': return 'green';
      case 'uploading':
      case 'processing': return 'blue';
      case 'pending': return 'gray';
      case 'uploaded': return 'green';
      case 'error': return 'red';
      default: return 'gray';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    if (i < 0 || i >= sizes.length) return bytes + ' Bytes';
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // [KEY FIX] Construct the full key path including the namespace.
  // The 't' function will now correctly find this translation.
  const statusTextKey = `creatorStudio:fileStatus.${fileData.status}`; 
  const retryButtonTextKey = t('common.retry');
  const removeButtonAriaLabel = t('common.removeFileAriaLabel', { fileName: fileData.file.name });

  return (
    <div
      className={clsx(
        "flex items-center justify-between p-3 bg-white rounded-xl",
        "border-2 border-black",
        "transition-colors duration-150 ease-in-out hover:bg-slate-50"
      )}
    >
      <div className="flex items-center gap-3 overflow-hidden min-w-0 flex-grow">
        <FileText size={28} className="text-slate-500 shrink-0" />
        <div className="flex-grow overflow-hidden mr-2">
          <p
            className="text-sm font-semibold text-slate-800 truncate"
            title={fileData.file.name}
          >
            {fileData.file.name}
          </p>
          <p className="text-xs text-slate-500">
            {formatFileSize(fileData.file.size)}
            {fileData.tokenCount !== undefined && (
              <span className="ml-1.5 text-xs text-slate-400">
                ({t('fileItem.tokenCount', { count: fileData.tokenCount })})
              </span>
            )}
          </p>
        </div>
        <Badge
          // The key is now correctly looked up within the 'agentBuilder' namespace
          textKey={statusTextKey}
          accentColor={getStatusBadgeColor()}
          className="text-xs shrink-0"
        />
      </div>

      <div className="flex items-center gap-3 shrink-0 ml-3">
        {fileData.status === 'uploading' && fileData.progress !== undefined && (
          <div className="flex items-center gap-2 w-28">
            <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden border-2 border-black">
              <div
                className="h-full bg-indigo-600 transition-all duration-150"
                style={{ width: `${fileData.progress}%` }}
                role="progressbar"
                aria-valuenow={fileData.progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('fileItem.uploadProgressAriaLabel', { progress: fileData.progress })}
              />
            </div>
            <span className="text-xs font-medium text-slate-600 w-8 text-right shrink-0">{fileData.progress}%</span>
          </div>
        )}

        {fileData.status === 'error' && (
          <>
            {fileData.errorMessage && (
              <Tooltip text={t(fileData.errorMessage)}>
                <AlertTriangle size={20} className="text-red-500 cursor-help shrink-0" />
              </Tooltip>
            )}
            {onRetry && (
              <Button
                textKey={retryButtonTextKey}
                onClick={() => onRetry(fileData.id)}
                variant="ghost"
                size="sm"
                icon={<RotateCcw size={14} />}
                className="text-xs !text-indigo-600 hover:!text-indigo-700 shrink-0"
                ariaLabel={t(retryButtonTextKey)}
              />
            )}
          </>
        )}
        <Tooltip text={removeButtonAriaLabel}>
            <IconButton
            icon={<X size={18} />}
            ariaLabel={removeButtonAriaLabel}
            onClick={() => onRemove(fileData.id)}
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-red-600 focus-visible:ring-red-500 shrink-0"
            />
        </Tooltip>
      </div>
    </div>
  );
};